import { Link } from "react-router-dom";
import { Phone, MapPin, Instagram, Facebook, Twitter } from "lucide-react";
import logoJpg from "@/assets/logo.png";
import logoWebp from "@/assets/logo.png.webp";
import logoAvif from "@/assets/logo.png.avif";
import ResponsiveImage from "@/components/ResponsiveImage";
import { trackPhoneCall } from "@/lib/analytics";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-accent text-accent-foreground pt-16 pb-8">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Company Info */}
          <div>
            <ResponsiveImage src={logoJpg} webp={logoWebp} avif={logoAvif} alt="New Business" className="h-16 w-auto mb-4" width={64} height={64} />
            <h3 className="text-xl font-bold mb-4">New Business</h3>
            <p className="text-sm text-accent-foreground/80 leading-relaxed">
              رواد في صناعة أبواب WPC في السعودية. نقدم منتجات عالية الجودة بتصميم عصري وتقنية متقدمة.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-6">روابط سريعة</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-sm text-accent-foreground/80 hover:text-primary transition-colors">
                  الرئيسية
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-sm text-accent-foreground/80 hover:text-primary transition-colors">
                  من نحن
                </Link>
              </li>
              <li>
                <Link to="/products" className="text-sm text-accent-foreground/80 hover:text-primary transition-colors">
                  المنتجات
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-sm text-accent-foreground/80 hover:text-primary transition-colors">
                  تواصل معنا
                </Link>
              </li>
            </ul>
          </div>

          {/* Products */}
          <div>
            <h4 className="text-lg font-bold mb-6">منتجاتنا</h4>
            <ul className="space-y-3">
              <li className="text-sm text-accent-foreground/80">أبواب داخلية كلاسيك</li>
              <li className="text-sm text-accent-foreground/80">أبواب الحمامات</li>
              <li className="text-sm text-accent-foreground/80">أبواب ديكورية فاخرة</li>
              <li className="text-sm text-accent-foreground/80">أبواب CNC محفورة</li>
              <li className="text-sm text-accent-foreground/80">أبواب بدون حلق</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-bold mb-6">تواصل معنا</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Phone className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="text-sm font-medium">اتصل بنا</p>
                  <a
                    href="tel:+966552351116"
                    onClick={() => trackPhoneCall("footer")}
                    className="text-sm text-accent-foreground/80 hover:text-primary transition-colors"
                    dir="ltr"
                  >
                    +966 55 235 1116
                  </a>

                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="text-sm font-medium">العنوان</p>
                  <p className="text-sm text-accent-foreground/80">حي طويق - صناعية طويق</p>
                  <p className="text-sm text-accent-foreground/80">الرياض، المملكة العربية السعودية</p>
                </div>
              </li>
            </ul>
            
            {/* Social Media */}
            <div className="flex gap-4 mt-6">
              <a href="#" aria-label="Instagram" className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center hover:bg-primary/20 transition-colors">
                <Instagram className="h-5 w-5 text-primary" />
              </a>
              <a href="#" aria-label="Facebook" className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center hover:bg-primary/20 transition-colors">
                <Facebook className="h-5 w-5 text-primary" />
              </a>
              <a href="#" aria-label="Twitter" className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center hover:bg-primary/20 transition-colors">
                <Twitter className="h-5 w-5 text-primary" />
              </a>
            </div>
          </div>
        </div>

        {/* Map */}
        <div className="mb-8 rounded-lg overflow-hidden h-64">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3628.4940920802383!2d46.51136411022047!3d24.572138878027353!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMjTCsDM0JzE5LjciTiA0NsKwMzAnNTAuMiJF!5e0!3m2!1sen!2ssa!4v1762851831276!5m2!1sen!2ssa"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="موقع المصنع"
          />
        </div>

        {/* Copyright + Legal Links */}
        <div className="border-t border-accent-foreground/20 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-3">
            <p className="text-sm text-accent-foreground/60">
              جميع الحقوق محفوظة © {currentYear} New Business - أبواب WPC المتكامل
            </p>

            <div className="flex items-center gap-3">
              <Link to="/privacy" className="text-xs text-accent-foreground/60 hover:text-primary transition-colors">إعدادات الخصوصية</Link>
              <Link to="/privacy-policy" className="text-xs text-accent-foreground/60 hover:text-primary transition-colors">سياسة الخصوصية</Link>
              <Link to="/terms" className="text-xs text-accent-foreground/60 hover:text-primary transition-colors">الشروط والأحكام</Link>

              <a href="https://rozanah.com" target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs text-muted-foreground/50 hover:text-bronze transition-colors group bg-white/5 px-3 py-1 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-code-xml w-3 h-3">
                  <path d="m18 16 4-4-4-4"></path>
                  <path d="m6 8-4 4 4 4"></path>
                  <path d="m14.5 4-5 16"></path>
                </svg>
                <span>تطوير</span>
                <span className="font-bold group-hover:underline">rozanah.com</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
